import { useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    makeStyles,
    IconButton,
    DialogContent,
    DialogActions,
    Button,
    Paper,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Draggable from 'react-draggable';
import { Trans, t } from '@lingui/macro';
import { timeRangeFormatter } from '../../utilities';
import DataTable from '../data-table/DataTable';
import { useQueryLoader } from '../data-table/Loaders';
import { FORMATTER_TYPES } from '../../utilities/FormattingUtilities';

const useStyles = makeStyles((theme) => ({
    table: {
        width: '100%',
    },
    title: {
        margin: 0,
        padding: theme.spacing(2),
        cursor: 'move',
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
}));

const columns = [
    {
        type: 'text',
        title: t`Time Stamp`,
        formatter: FORMATTER_TYPES.DATETIME,
        width: 240,
        mapping: 'time',
        sortable: true,
    },
    {
        type: 'text',
        title: t`Facility`,
        width: 120,
        mapping: 'facility',
        sortable: false,
    },
    {
        type: 'text',
        title: t`Severity`,
        width: 120,
        mapping: 'severity',
        sortable: true,
    },
    {
        type: 'text',
        title: t`Message`,
        flex: 1,
        minWidth: 400,
        mapping: 'message',
        sortable: false,
    },
];

const sorters = [{ property: 'severity', direction: 'desc' }];

function useSyslogDataSource(timeRange, entity) {
    const { start, end } = timeRange;
    const { type, uuid } = entity;

    return useMemo(() => {
        return {
            url: '/query/sysloglist',
            params: {
                type,
                server: uuid,
                time: true,
                metrics: ['time', 'facility', 'severity', 'message'],
                granularity: 'chartSpan.agg.sub',
            },
            totalProperty: 'count',
            // Override the time filter's start/end parameters
            overrideParams: {
                start,
                end,
                rowLimit: 250,
            },
        };
    }, [start, end, type, uuid]);
}

function PaperComponent(props) {
    return (
        <Draggable handle="#syslog-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}

function SyslogTable({ timeRange, entity }) {
    const syslogDataSource = useSyslogDataSource(timeRange, entity);
    const loader = useQueryLoader(syslogDataSource);
    const classes = useStyles();

    return (
        <div className={classes.table}>
            <DataTable tableId="SyslogEvents" visibleRows={10} loader={loader} columns={columns} sorters={sorters} />
        </div>
    );
}

export default function SyslogDialog({ open, onClose, timeRange, entity }) {
    const classes = useStyles();
    const timeRangeStr = timeRangeFormatter(timeRange);
    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby="syslog-dialog-title"
        >
            <DialogTitle className={classes.title} id="syslog-dialog-title">
                {timeRangeStr}
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <SyslogTable timeRange={timeRange} entity={entity} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" autoFocus>
                    <Trans>Close</Trans>
                </Button>
            </DialogActions>
        </Dialog>
    );
}
